
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { GymTable } from '@/components/gyms/gym-table';
import { AddGymDialog } from '@/components/gyms/add-gym-dialog';
import { PlusIcon, AlertCircle } from 'lucide-react';
import { useTenantSubscription } from '@/api/tenants';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { PageHeader } from '@/components/shared/page-header';

export default function GymsPage() {
  const { t } = useTranslation();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // Get subscription to check gym limits
  const { data: subscription } = useTenantSubscription();

  // Check if gym limit is reached
  const gymLimitReached = subscription
    ? subscription.gymCount >= subscription.gymLimit
    : false;
  const gymsRemaining = subscription
    ? subscription.gymLimit - subscription.gymCount
    : null;

  const handleGymAdded = () => {
    // Increment the refresh key to force a re-render of the GymTable
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('gyms.title')}
        description={
          subscription
            ? t('gyms.gymUsage', {
                current: subscription.gymCount,
                limit: subscription.gymLimit,
              })
            : undefined
        }
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button
                  onClick={() => setShowAddDialog(true)}
                  disabled={gymLimitReached}
                  className="w-full sm:w-auto"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  {t('gyms.addGym')}
                </Button>
              </span>
            </TooltipTrigger>
            {gymLimitReached && (
              <TooltipContent>
                <p>{t('gyms.gymLimitReached')}</p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </PageHeader>

      {gymLimitReached && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {t('gyms.gymLimitReachedMessage')}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('gyms.gymsList')}</CardTitle>
        </CardHeader>
        <CardContent>
          <GymTable key={refreshKey} />
        </CardContent>
      </Card>

      <AddGymDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
        onGymAdded={handleGymAdded}
      />
    </div>
  );
}
